<template>
  <section v-if="ride.text || host" class="journey-story">
    <header v-if="ride.text" class="journey-section-head">
      <p class="journey-section-kicker">{{ t('journeyStoryKicker') }}</p>
      <h2 class="journey-section-title">{{ t('journeyStoryTitle') }}</h2>
    </header>
    <div :class="['journey-story-grid', { 'journey-story-single': !ride.text || !host }]">
      <div v-if="ride.text" class="journey-story-text">
        <span class="journey-story-quote" aria-hidden="true">&ldquo;</span>
        <PosterStory :ride="ride"/>
      </div>
      <aside v-if="host" class="journey-story-host">
        <PosterHostCard/>
      </aside>
    </div>
  </section>
</template>

<script>
import { mapGetters } from "vuex";
import { translationMixin } from "@/mixins/translationMixin";
import PosterStory from "@/designs/poster/PosterStory";
import PosterHostCard from "@/designs/poster/PosterHostCard";

/**
 * The journey design's "about the trip": the ride description as an
 * editorial column under a big quote mark, with the ride manager's card
 * beside it on a desktop and under it on a phone. The pieces are the
 * poster's (cut to a few lines with show more), dressed for this page.
 */
export default {
  name: 'JourneyStory',
  mixins: [translationMixin],
  components: { PosterStory, PosterHostCard },
  props: {
    ride: { type: Object, required: true },
  },
  computed: {
    ...mapGetters('presentUserInfo', { host: 'userExtraInformation' }),
  },
};
</script>

<style scoped>
/* Sizes in px: the app's root font is 10px, so rem here would be tiny. */
.journey-story {
  max-width: 1120px;
  margin: 0 auto;
  padding: clamp(28px, 6vw, 56px) clamp(16px, 5vw, 56px) 8px;
}

.journey-story-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.7fr) minmax(0, 1fr);
  gap: clamp(20px, 4vw, 48px);
  align-items: start;
}

.journey-story-single {
  grid-template-columns: minmax(0, 1fr);
  max-width: 760px;
}

@media (max-width: 860px) {
  .journey-story-grid {
    grid-template-columns: minmax(0, 1fr);
  }
}

.journey-story-text {
  position: relative;
  padding: clamp(28px, 4vw, 40px) clamp(22px, 4vw, 40px) 18px;
  border-radius: 28px;
  background: #fff;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04), 0 30px 60px -40px rgba(13, 17, 23, 0.45);
}

.journey-story-quote {
  position: absolute;
  top: -18px;
  inset-inline-start: 26px;
  font-family: Georgia, "Times New Roman", serif;
  font-size: 110px;
  line-height: 1;
  color: var(--primary-color, #07a0c3);
  opacity: 0.9;
}

/* The poster's pieces, dressed for this page. */
.journey-story-text ::v-deep .poster-story {
  max-width: none;
  margin: 0;
  padding: 0;
  background: none;
  border-radius: 0;
}

.journey-story-host ::v-deep .poster-host {
  max-width: none;
  margin: 0;
  padding: 22px;
  border-radius: 28px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04), 0 30px 60px -40px rgba(13, 17, 23, 0.45);
}
</style>
